import { useState, useMemo, useCallback, useEffect } from 'react';

interface PaginationData {
  total?: number;
  totalItems?: number;
  page?: number;
  currentPage?: number;
  limit?: number;
  itemsPerPage?: number;
  totalPages?: number;
  pages?: number;
}

interface BackendPaginationResult {
  currentPage: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  showingFrom: number;
  showingTo: number;
  totalItems: number;
  goToPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Hook for managing server-side pagination
 * Accepts backend pagination data and uses provided values directly
 * Handles multiple response structures:
 * 1. Separate meta: { data: [...], meta: { total, page, limit, totalPages } }
 * 2. Flat response: { data: [...], total, page, limit, totalPages }
 * 3. Pagination object: { data: [...], pagination: { total, page, limit, totalPages } }
 */
export default function useBackendPagination(
  paginationData: PaginationData | number = 0,
  itemsPerPage: number = 10,
  initialPage: number = 1
): BackendPaginationResult {
  // Support both old API (number) and new API (object)
  const data = typeof paginationData === 'object' ? paginationData : {};

  // Extract values from backend response with fallbacks
  const totalItems = (data.total ?? data.totalItems ?? 0) as number;
  const backendPage = (data.page ?? data.currentPage ?? initialPage) as number;
  const limit = (data.limit ?? data.itemsPerPage ?? itemsPerPage) as number;
  const backendTotalPages = (data.totalPages ?? data.pages) as
    | number
    | undefined;

  const [currentPage, setCurrentPage] = useState<number>(backendPage);

  useEffect(() => {
    setCurrentPage(backendPage);
  }, [backendPage]);

  // Use backend totalPages if provided, otherwise calculate
  const totalPages = useMemo(() => {
    return backendTotalPages ?? Math.ceil(totalItems / limit);
  }, [backendTotalPages, totalItems, limit]);

  const showingFrom = useMemo(() => {
    return totalItems === 0 ? 0 : (currentPage - 1) * limit + 1;
  }, [currentPage, limit, totalItems]);

  const showingTo = useMemo(() => {
    return Math.min(currentPage * limit, totalItems);
  }, [currentPage, limit, totalItems]);

  // Navigation handlers
  const goToPage = useCallback(
    (page: number) => {
      const validPage = Math.max(1, Math.min(page, totalPages || 1));
      setCurrentPage(validPage);
    },
    [totalPages]
  );

  const goToNextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const goToPreviousPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const goToLastPage = useCallback(() => {
    setCurrentPage(totalPages || 1);
  }, [totalPages]);

  // Boolean flags
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return {
    currentPage,
    totalPages,
    hasPrevious,
    hasNext,
    showingFrom,
    showingTo,
    totalItems,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    setCurrentPage,
  };
}
